import { useState, useEffect, useRef } from "react";
import {
  Shield,
  Activity,
  Server,
  Plus,
  Edit,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  Grid3X3,
  List,
  Key,
  ExternalLink,
  CheckCircle,
  XCircle,
  Settings,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { MetricCard, MetricCardGrid } from "../../widgets/MetricCard";
import SSOProviderForm from "../../components/sso/SSOProviderForm";
import ConfirmationDialog from "../../widgets/ConfirmationModal";
import LoginSettingsModal from "../../components/auth/LoginSettingsModal";
import ssoProvidersService, {
  SSOProvider,
  CreateSSOProviderData,
  UpdateSSOProviderData,
  SSOProviderListParams,
  SSOProviderStats,
} from "../../services/sso-providers.service";
import Loader from "../../widgets/Loader";
import Tooltip from "../../widgets/ToolTip";

function SSOConfig() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<SSOProvider | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<SSOProvider | null>(
    null
  );
  const [providers, setProviders] = useState<SSOProvider[]>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(12);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [searchFilters, setSearchFilters] = useState({
    provider_name: "",
    display_name: "",
  });
  const [providerStats, setProviderStats] = useState<SSOProviderStats>({
    total_providers: 0,
    active_providers: 0,
    inactive_providers: 0,
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [showLoginSettings, setShowLoginSettings] = useState(false);

  const isInitialMount = useRef(true);

  // Consolidated fetch effect for providers
  useEffect(() => {
    if (isInitialMount.current) {
      fetchProviders();
      isInitialMount.current = false;
    } else if (Object.values(searchFilters).some((filter) => filter !== "")) {
      const debounceTimer = setTimeout(() => {
        fetchProviders();
      }, 500);
      return () => clearTimeout(debounceTimer);
    } else {
      fetchProviders();
    }
  }, [currentPage, searchFilters]);

  useEffect(() => {
    fetchProviderStats();
  }, []);

  const fetchProviders = async () => {
    try {
      setIsLoadingProviders(true);
      const params: SSOProviderListParams = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        ...(searchFilters.provider_name && {
          provider_name: searchFilters.provider_name,
        }),
        ...(searchFilters.display_name && {
          display_name: searchFilters.display_name,
        }),
      };

      const response = await ssoProvidersService.getProviders(params);
      setProviders(response.data.login_provider_list);
      setTotalCount(response.data.total_count);
    } catch (error) {
      console.error("Error fetching SSO providers:", error);
      toast.error("Failed to load SSO providers");
    } finally {
      setIsLoadingProviders(false);
    }
  };

  const fetchProviderStats = async () => {
    try {
      setIsLoadingStats(true);
      const stats = await ssoProvidersService.getProviderStats();
      setProviderStats(stats);
    } catch (error) {
      console.error("Error fetching provider stats:", error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const handleAddProvider = () => {
    setSelectedProvider(null);
    setIsFormOpen(true);
  };

  const handleEditProvider = (provider: SSOProvider) => {
    setSelectedProvider(provider);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedProvider(null);
  };

  const handleFormSubmit = async (
    data: CreateSSOProviderData | UpdateSSOProviderData
  ) => {
    setIsLoading(true);
    try {
      if (selectedProvider) {
        // Update existing provider
        await ssoProvidersService.updateProvider(
          selectedProvider._id,
          data as UpdateSSOProviderData
        );
        handleCloseForm();
        toast.success(`Provider "${data.display_name}" updated successfully!`);
        await fetchProviders();
        await fetchProviderStats();
      } else {
        // Add new provider
        await ssoProvidersService.createProvider(data as CreateSSOProviderData);
        handleCloseForm();
        toast.success(`Provider "${data.display_name}" created successfully!`);
        await fetchProviders();
        await fetchProviderStats();
      }
    } catch (error: any) {
      console.error("Error saving provider:", error);
      if (error.response?.data?.error?.error_description) {
        toast.error(error.response.data.error.error_description);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(
          selectedProvider
            ? "Failed to update provider"
            : "Failed to create provider"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (provider: SSOProvider) => {
    setProviderToDelete(provider);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (providerToDelete) {
      try {
        await ssoProvidersService.deleteProvider(providerToDelete._id);
        setProviderToDelete(null);
        setShowDeleteConfirm(false);
        toast.success(
          `Provider "${providerToDelete.display_name}" deleted successfully!`
        );
        await fetchProviders();
        await fetchProviderStats();
      } catch (error: any) {
        console.error("Error deleting provider:", error);
        if (error.response?.data?.error?.error_description) {
          toast.error(error.response.data.error.error_description);
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Failed to delete provider");
        }
      }
    }
  };

  const handleCancelDelete = () => {
    setProviderToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleToggleProvider = async (provider: SSOProvider) => {
    try {
      const newStatus = !provider.enable;
      await ssoProvidersService.toggleProviderStatus(provider._id, newStatus);
      toast.success(
        `Provider "${provider.display_name}" ${newStatus ? "enabled" : "disabled"} successfully!`
      );
      await fetchProviders();
      await fetchProviderStats();
    } catch (error: any) {
      console.error("Error toggling provider status:", error);
      if (error.response?.data?.error?.error_description) {
        toast.error(error.response.data.error.error_description);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to toggle provider status");
      }
    }
  };

  const handleSearchChange = (field: string, value: string) => {
    setSearchFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  // Grid View Component
  const GridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {providers.map((provider) => (
        <div
          key={provider._id}
          className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200"
        >
          {/* Provider Header */}
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                {provider.logo_url ? (
                  <img
                    src={provider.logo_url}
                    alt={provider.display_name}
                    className="w-10 h-10 rounded-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextElementSibling?.classList.remove(
                        "hidden"
                      );
                    }}
                  />
                ) : null}
                <div
                  className={`w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center ${
                    provider.logo_url ? "hidden" : ""
                  }`}
                >
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">
                    {provider.display_name}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {provider.provider_name}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {provider.enable && provider.is_active ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
              </div>
            </div>
          </div>

          {/* Provider Details */}
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Server className="w-4 h-4" />
              <span className="truncate">{provider.issuer}</span>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Key className="w-4 h-4" />
              <span className="truncate">Client ID: ••••••••{provider.client_id.slice(-4)}</span>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                {new Date(provider.createdAt).toLocaleDateString()}
              </span>
              <div className="flex items-center gap-2">
                <Tooltip content={provider.enable ? "Disable provider" : "Enable provider"}>
                  <button
                    onClick={() => handleToggleProvider(provider)}
                    className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors
                        ${provider.enable ? "bg-green-600" : "bg-gray-400"}`}
                    type="button"
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform
                        ${provider.enable ? "translate-x-4" : "translate-x-1"}`}
                    />
                  </button>
                </Tooltip>
                <Tooltip content="Edit">
                <button
                  onClick={() => handleEditProvider(provider)}
                  className="text-blue-600 hover:text-blue-900 p-1 rounded"
                  type="button"
                >
                  <Edit className="w-4 h-4" />
                </button>
                </Tooltip>
                <Tooltip content="Delete">
                <button
                  onClick={() => handleDeleteClick(provider)}
                  className="text-red-600 hover:text-red-900 p-1 rounded"
                  type="button"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // List View Component
  const ListView = () => (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Provider
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Provider Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Issuer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created At
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {providers.map((provider) => (
              <tr key={provider._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {provider.logo_url ? (
                      <img
                        src={provider.logo_url}
                        alt={provider.display_name}
                        className="w-8 h-8 rounded-full object-cover mr-3"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          e.currentTarget.nextElementSibling?.classList.remove(
                            "hidden"
                          );
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-3 ${
                        provider.logo_url ? "hidden" : ""
                      }`}
                    >
                      <Shield className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {provider.display_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {provider.standard_type}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                    {provider.provider_name}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      provider.enable && provider.is_active
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {provider.enable && provider.is_active ? (
                      <>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3 mr-1" />
                        Inactive
                      </>
                    )}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <span className="truncate max-w-xs">{provider.issuer}</span>
                    <ExternalLink className="w-3 h-3 text-gray-400" />
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(provider.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <Tooltip content={provider.enable ? "Disable provider" : "Enable provider"}>
                      <button
                        onClick={() => handleToggleProvider(provider)}
                        className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors
                        ${provider.enable ? "bg-green-600" : "bg-gray-400"}`}
                        type="button"
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform
                        ${provider.enable ? "translate-x-4" : "translate-x-1"}`}
                        />
                      </button>
                    </Tooltip>
                    <Tooltip content="Edit">
                    <button
                      onClick={() => handleEditProvider(provider)}
                      className="text-blue-600 hover:text-blue-900 p-1 rounded"
                      type="button"

                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    </Tooltip>
                    <Tooltip content="Delete">
                    <button
                      onClick={() => handleDeleteClick(provider)}
                      className="text-red-600 hover:text-red-900 p-1 rounded"
                      type="button"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
      <div className="px-6 py-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              SSO Configuration
            </h1>
            <p className="text-xs text-slate-600">
              Manage Single Sign-On providers
            </p>
          </div>
          <div className="flex gap-3">
                <button
                  onClick={() => setShowLoginSettings(true)}
                  className="bg-slate-500 hover:bg-slate-600 text-white px-3 py-1.5 rounded-md 
             flex items-center gap-1.5 text-sm transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Login Settings
                </button>

                <button
                  onClick={handleAddProvider}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md 
             flex items-center gap-1.5 text-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add SSO Provider
                </button>
          </div>
        </div>
        </div>
        </div>

        {/* Metric Cards */}
        <MetricCardGrid>
          <MetricCard
            title="Total Providers"
            value={isLoadingStats ? "..." : providerStats?.total_providers}
            icon={Server}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
          />
          <MetricCard
            title="Active Providers"
            value={isLoadingStats ? "..." : providerStats?.active_providers}
            icon={Activity}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
          />
          <MetricCard
            title="Inactive Providers"
            value={isLoadingStats ? "..." : providerStats?.inactive_providers}
            icon={Shield}
            iconColor="text-red-600"
            iconBgColor="bg-red-100"
          />
        </MetricCardGrid>

        {/* Search Filters */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Search Filters
            </h3>
            <div className="flex items-center gap-2">
              <Tooltip content="Grid View">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === "grid"
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-400 hover:text-gray-600"
                }`}
                type="button"
              >
                <Grid3X3 className="w-5 h-5" />
              </button>
              </Tooltip>
              <Tooltip content="List View">
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === "list"
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-400 hover:text-gray-600"
                }`}
                type="button"
              >
                <List className="w-5 h-5" />
              </button>
              </Tooltip>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by provider name..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchFilters.provider_name}
                onChange={(e) =>
                  handleSearchChange("provider_name", e.target.value)
                }
              />
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by display name..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchFilters.display_name}
                onChange={(e) =>
                  handleSearchChange("display_name", e.target.value)
                }
              />
            </div>
          </div>
        </div>

        {/* Providers Content */}
        {isLoadingProviders ? (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <Loader />
            <span className="mt-4 text-gray-600 font-semibold">
              Loading SSO providers...
            </span>
          </div>
        ) : providers.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No SSO providers found
            </h3>
            <p className="text-gray-500 mb-6">
              Get started by creating your first SSO provider configuration.
            </p>
            <button
              onClick={handleAddProvider}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 mx-auto transition-colors duration-150"
            >
              <Plus className="w-4 h-4" />
              Add SSO Provider
            </button>
          </div>
        ) : (
          <>
            {viewMode === "grid" ? <GridView /> : <ListView />}

            {/* Pagination */}
            {totalPages > 0 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startItem}</span> to{" "}
                  <span className="font-medium">{endItem}</span> of{" "}
                  <span className="font-medium">{totalCount}</span> providers
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* SSO Provider Form Modal */}
      <SSOProviderForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        provider={selectedProvider}
        onSubmit={handleFormSubmit}
        isLoading={isLoading}
      />

      {/* Confirmation Modal for Delete */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete SSO Provider"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        description={`Are you sure you want to delete the SSO provider <strong>${providerToDelete?.display_name}</strong>? This action cannot be undone and may affect user authentication.`}
        iconName="warning"
      />

      {/* Login Settings Modal */}
      <LoginSettingsModal
        isOpen={showLoginSettings}
        onClose={() => setShowLoginSettings(false)}
        onSave={async () => {
          await fetchProviders();
          await fetchProviderStats();
        }}
      />
    </div>
  );
}

export default SSOConfig;
